/**
 * Sanitizes a filename to be safe for use in file system operations.
 * Removes path separators, dangerous characters, and ensures valid filename format.
 *
 * @param filename - The filename to sanitize
 * @param maxLength - Maximum length for the filename (default: 255)
 * @returns Sanitized filename, or "download" if the result would be empty
 */
export function sanitizeFilename(
  filename: string,
  maxLength: number = 255
): string {
  if (!filename || typeof filename !== "string") {
    return "download";
  }

  // Remove null bytes and control characters (except newline, tab, carriage return)
  let sanitized = filename.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, "");

  // Remove path separators (Windows and Unix)
  sanitized = sanitized.replace(/[\/\\]/g, "");

  // Remove or replace reserved characters for Windows
  // Reserved: < > : " | ? *
  sanitized = sanitized.replace(/[<>:"|?*]/g, "_");

  // Remove leading/trailing spaces and dots (Windows doesn't allow these)
  sanitized = sanitized.trim().replace(/^\.+|\.+$/g, "");

  // Replace multiple consecutive spaces/underscores with single underscore
  sanitized = sanitized.replace(/[\s_]+/g, "_");

  // Limit length
  if (sanitized.length > maxLength) {
    // Try to preserve extension if possible
    const lastDot = sanitized.lastIndexOf(".");
    if (lastDot > 0 && lastDot < sanitized.length - 1) {
      const ext = sanitized.substring(lastDot);
      const name = sanitized.substring(0, lastDot);
      const maxNameLength = maxLength - ext.length;
      if (maxNameLength > 0) {
        sanitized = name.substring(0, maxNameLength) + ext;
      } else {
        sanitized = sanitized.substring(0, maxLength);
      }
    } else {
      sanitized = sanitized.substring(0, maxLength);
    }
  }

  // If result is empty or only special characters, return a default
  if (!sanitized || sanitized.length === 0) {
    return "download";
  }

  return sanitized;
}

